import styled from '@emotion/styled';
import { Button } from 'antd';
import { ReactNode, useEffect, useState } from 'react';
import {
  FaBell,
  FaBuilding,
  FaChartArea,
  FaChevronRight,
  FaCog,
  FaCrown,
  FaFileAlt,
  FaMoneyBillWaveAlt,
  FaSyncAlt,
  FaUser,
  FaUserTie,
} from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../../../routes';
import { ORANGE, PRIMARY, YELLOW } from '../../../../shared/colors';
import { getSAdminReporting } from '../../../dashboard/network';
import { Layout } from '../../../shared/Layout';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 92% 8%, rgba(255, 176, 0, 0.08), transparent 22%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  .bread-crum span {
    color: var(--mt-text);
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: var(--mt-row-odd);
    color: var(--mt-text);
  }

  .even-row {
    background-color: var(--mt-row-even);
    color: var(--mt-text);
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: var(--mt-row-hover) !important;
      cursor: pointer;
      color: var(--mt-text);
    }
  }

  .content {
    padding: 12px;
    width: 100%;
    background: transparent;
    color: var(--mt-text);
  }

  .admin-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .hero-badge {
    min-width: 210px;
    border: 1px solid rgba(255, 176, 0, 0.28);
    border-radius: 8px;
    padding: 13px;
    color: #fff7e8;
    background: rgba(255, 255, 255, 0.06);
  }

  .hero-badge span {
    display: block;
    color: rgba(255, 247, 232, 0.62);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .hero-badge strong {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 7px;
    color: ${YELLOW};
    font-family: Poppins-B;
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .admin-hero h1 {
    margin: 6px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
  }

  .admin-hero p {
    max-width: 760px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
    line-height: 1.55;
  }

  .kpi-box {
    min-height: 92px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);

    .icon {
      display: grid;
      place-items: center;
      width: 42px;
      height: 42px;
      border: 1px solid rgba(255, 176, 0, 0.26);
      border-radius: 8px;
      color: var(--mt-warning);
      background: var(--mt-surface);
    }

    .title {
      color: var(--mt-muted);
      font-size: 11px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }

    .value {
      display: block;
      margin-top: 6px;
      color: var(--mt-text);
      font-family: Poppins-B;
      font-size: 24px;
    }

    .meta {
      margin-top: 5px;
      color: var(--mt-muted);
      font-size: 11px;
    }
  }

  .admin-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(320px, 0.6fr);
    gap: 12px;
    margin-top: 12px;
  }

  .insight-panel,
  .quick-panel,
  .alert-panel {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 16px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .insight-panel h2,
  .quick-panel h2,
  .alert-panel h2 {
    margin: 0;
    color: var(--mt-warning);
    font-family: Poppins-B;
    font-size: 18px;
  }

  .insight-panel p,
  .quick-panel p,
  .alert-panel p {
    max-width: 860px;
    margin: 8px 0 0;
    color: var(--mt-muted);
    line-height: 1.6;
  }

  .health-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 14px;
  }

  .health-card {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 12px;
    background: var(--mt-surface);
  }

  .health-card span {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .health-card strong {
    display: block;
    margin-top: 5px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 18px;
  }

  .quick-list,
  .alert-list {
    display: grid;
    gap: 9px;
    margin-top: 14px;
  }

  .quick-link,
  .alert-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 12px;
    background: var(--mt-surface);
  }

  .quick-link {
    color: var(--mt-text);
    cursor: pointer;
  }

  .quick-link:hover {
    border-color: rgba(255, 176, 0, 0.38);
    color: ${YELLOW};
  }

  .quick-link div,
  .alert-item div {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .quick-icon,
  .alert-icon {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    color: ${YELLOW};
    background: rgba(255, 176, 0, 0.12);
  }

  .alert-item strong {
    display: block;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .alert-item span {
    display: block;
    color: var(--mt-muted);
    font-size: 12px;
  }

  .refresh-button {
    height: 40px;
    border: 0;
    border-radius: 999px;
    background: linear-gradient(135deg, ${ORANGE}, ${YELLOW});
    color: #07141c;
    font-family: Poppins-B;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  @media (max-width: 760px) {
    .content {
      padding: 18px 12px;
    }

    .admin-hero,
    .admin-grid {
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }

    .health-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export const DashboardPage = () => {
  const [donnees, setDonnees] = useState<any>([]);
  const history = useHistory();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = () => {
    getSAdminReporting().then(({ data }) => {
      setDonnees(data);
    });
  };
  const KPIContainer = ({
    meta,
    title,
    value,
    icon,
  }: {
    meta?: string;
    title: string;
    value: string;
    icon: ReactNode;
  }) => {
    return (
      <div className='kpi-box'>
        <div className='icon'>{icon}</div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}
        >
          <div className='title'>{title}</div>
          <div className='value'>{value}</div>
          {meta && <div className='meta'>{meta}</div>}
        </div>
      </div>
    );
  };
  const usersCount = Number(donnees.assets || 0);
  const clientsCount = Number(donnees.clients || 0);
  const titresCount = Number(donnees.titres || 0);
  const revenue = Number(donnees.chiffre_a || 0);
  const conversionRatio = usersCount ? Math.round((clientsCount / usersCount) * 100) : 0;
  const revenuePerClient = clientsCount ? Math.round(revenue / clientsCount) : 0;

  const quickLinks = [
    {
      icon: <FaUserTie />,
      label: 'Clients MoranjETrade',
      route: ROUTES.ADMIN.CONSOLE,
    },
    {
      icon: <FaCrown />,
      label: 'Packs et abonnements',
      route: ROUTES.ADMIN.PACK,
    },
    {
      icon: <FaCog />,
      label: 'Paramètres de commissions',
      route: ROUTES.ADMIN.PARAMETER,
    },
    {
      icon: <FaFileAlt />,
      label: 'Reporting opérations',
      route: ROUTES.ADMIN.REPORTING,
    },
  ];

  return (
    <Layout>
      <Container>
        <div className='content'>
          <div style={{ marginTop: 0 }}>
            <section className='admin-hero'>
              <div>
                <span className='eyebrow'>Administration</span>
                <h1>Dashboard admin</h1>
                <p>
                  Vue consolidée de la plateforme, des utilisateurs, des clients,
                  des titres et du chiffre d’affaires généré.
                </p>
              </div>
              <div className='hero-badge'>
                <span>État plateforme</span>
                <strong><FaBuilding /> Console active</strong>
              </div>
            </section>

            <div className='kpi-grid'>
              <KPIContainer
                title="Nombre d'utilisateurs"
                value={donnees.assets || '--'}
                meta='Accès plateforme'
                icon={<FaUser size={20} />}
              />
              <KPIContainer
                title='Nombre de clients'
                value={donnees.clients || '--'}
                meta='Sociétés suivies'
                icon={<FaUserTie size={20} />}
              />
              <KPIContainer
                title='Nombre de titres disponibles'
                value={donnees.titres || '--'}
                meta='Univers investissable'
                icon={<FaMoneyBillWaveAlt size={20} />}
              />
              <KPIContainer
                title="Chiffre d'affaire"
                value={revenue ? `${revenue.toLocaleString('fr-FR')} XAF` : '--'}
                meta='Revenus consolidés'
                icon={<FaChartArea size={20} />}
              />
            </div>
            <div className='admin-grid'>
              <section className='insight-panel'>
                <h2>Lecture opérationnelle</h2>
                <p>
                  Ce cockpit sert de point d’entrée pour suivre la base clients,
                  le catalogue de titres, la monétisation et les paramètres qui
                  alimentent les parcours de trading.
                </p>
                <div className='health-grid'>
                  <div className='health-card'>
                    <span>Clients / utilisateurs</span>
                    <strong>{conversionRatio || '--'}%</strong>
                  </div>
                  <div className='health-card'>
                    <span>Revenu par client</span>
                    <strong>{revenuePerClient ? `${revenuePerClient.toLocaleString('fr-FR')} XAF` : '--'}</strong>
                  </div>
                  <div className='health-card'>
                    <span>Titres par client</span>
                    <strong>{clientsCount ? (titresCount / clientsCount).toFixed(1) : '--'}</strong>
                  </div>
                </div>
              </section>

              <section className='quick-panel'>
                <h2>Accès rapides</h2>
                <p>Ouvrez les consoles les plus utilisées sans repasser par le menu.</p>
                <div className='quick-list'>
                  {quickLinks.map((link) => (
                    <button
                      className='quick-link'
                      key={link.label}
                      onClick={() => history.push(link.route)}
                      type='button'
                    >
                      <div>
                        <span className='quick-icon'>{link.icon}</span>
                        <strong>{link.label}</strong>
                      </div>
                      <FaChevronRight />
                    </button>
                  ))}
                </div>
              </section>
            </div>

            <section className='alert-panel' style={{ marginTop: 12 }}>
              <h2>Alertes de pilotage</h2>
              <p>
                Points d’attention calculés à partir des données disponibles.
              </p>
              <div className='alert-list'>
                <div className='alert-item'>
                  <div>
                    <span className='alert-icon'><FaBell /></span>
                    <span>
                      <strong>Paramètres de commissions</strong>
                      <span>Vérifier les taux après chaque changement de politique tarifaire.</span>
                    </span>
                  </div>
                  <Button className='refresh-button' onClick={() => history.push(ROUTES.ADMIN.PARAMETER)}>
                    Ouvrir
                  </Button>
                </div>
                <div className='alert-item'>
                  <div>
                    <span className='alert-icon'><FaSyncAlt /></span>
                    <span>
                      <strong>Données consolidées</strong>
                      <span>Dernière lecture calculée depuis le reporting super admin.</span>
                    </span>
                  </div>
                  <Button className='refresh-button' onClick={fetchData}>
                    Rafraîchir
                  </Button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </Container>
    </Layout>
  );
};
